import React from 'react';
import { StatusBadge } from '../../../components/ui';

// Stripe's own status strings for PaymentIntents, refunds, payouts and
// disputes, as the Connect webhook stores them. Modelled on
// src/views/finance/finance-v2/components/PayoutStatusBadge.js but not
// shared with it: that one maps Mercury payout phases, a different vocabulary.
const TONE_BY_STATUS = {
  succeeded: 'emerald',
  paid: 'emerald',
  won: 'emerald',
  collected: 'emerald',
  pending: 'amber',
  needs_response: 'amber',
  warning_needs_response: 'amber',
  requires_payment_method: 'amber',
  requires_action: 'amber',
  in_transit: 'sky',
  processing: 'sky',
  under_review: 'sky',
  warning_under_review: 'sky',
  failed: 'rose',
  returned: 'rose',
  lost: 'rose',
  canceled: 'slate',
  charge_refunded: 'slate',
  warning_closed: 'slate',
  refunded: 'slate',
};

// Restaurant-facing words for the same statuses, one badge for payments,
// refunds, payouts and (this table alone) application fees. The badge only
// ever gets a bare status string, never which of those objects it belongs
// to, so a status Stripe reuses across objects gets one label that has to
// read honestly in every place it can appear. Two of these are genuine
// compromises, not oversights:
//   - `succeeded` means "the payment went through" on a payment and "the
//     refund finished" on a refund. "Completed" is the one word that's true
//     either way; a table-aware label ("Paid" / "Refunded") would need the
//     badge to know its own object, which it doesn't.
//   - `requires_action` means "waiting on the customer to confirm" on a
//     payment and "waiting on more information from Stripe" on a refund.
//     "Needs another step" is deliberately generic rather than guessing
//     which side owes the next move.
const LABEL_BY_STATUS = {
  succeeded: 'Completed',
  paid: 'Paid out',
  won: 'Dispute won',
  collected: 'Collected',
  pending: 'Pending',
  needs_response: 'Needs your response',
  warning_needs_response: 'Question about a charge',
  requires_payment_method: 'Needs a new payment method',
  requires_action: 'Needs another step',
  in_transit: 'On its way',
  processing: 'Processing',
  under_review: 'Response under review',
  warning_under_review: 'Question under review',
  failed: 'Failed',
  // A payout the bank sent back after depositing it; the API flags it on
  // a `failed` payout, and the payouts table passes it through as this.
  returned: 'Returned',
  lost: 'Dispute lost',
  canceled: 'Canceled',
  charge_refunded: 'Refunded',
  warning_closed: 'Question resolved',
  refunded: 'Refunded',
};

// The same honest fallback as before — sentence case, spaces instead of
// underscores — for a status this map hasn't caught up to yet. It reads the
// raw enum rather than guessing at a meaning nobody has confirmed.
const fallbackLabel = (key) => {
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const ConnectStatusBadge = ({ status }) => {
  const key = String(status || '').toLowerCase();
  const label = key ? LABEL_BY_STATUS[key] || fallbackLabel(key) : 'Unknown';
  return <StatusBadge tone={TONE_BY_STATUS[key] || 'slate'} label={label} size="sm" dot />;
};

export default ConnectStatusBadge;
